import { queryOptions } from "@tanstack/react-query";
import type { ReportingAgentService } from "@/services/reporting-agent.service";
import type { AiDashboardSummary, ReportingConversationSummary } from "@/types/reporting-agent";
import { getQueryClient, peekQueryClient } from "./client";
import { FRESHNESS, withinAttempt } from "./policies";
import { httpStatus } from "./reports";
import { scopeIfReady, scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The Reporting Teammate's durable reads (NG-2811), every one under
 * `[slug, uid, 'reporting-agent', …]`. The live chat stays on its socket and
 * in `ReportingAgentStore`; these are the reads around it:
 *
 * - **generated reports**, `GET {ws}/reporting-agent/dashboards/` (every page):
 *   the reports the Teammate built, which the Reports page lists beside the
 *   built ones (NG-1588) and the viewer's breadcrumb names.
 * - **conversations**, `GET {ws}/reporting-agent/conversations/` (every page):
 *   the history picker.
 * - **artifact HTML** and **screenshot**, per generated report: the document the
 *   viewer frames, and the chat's thumbnail. An artifact never changes once
 *   written, so either is read once per session (until the cache drops it).
 *
 * All owner-scoped server-side. The generated reports and the artifacts are
 * **reference** data: a report the chat builds marks the list stale
 * (`markGeneratedReportsStale`, NG-1948), so the Reports page reads it again on
 * its next visit and keeps the old rows on screen meanwhile. The history picker
 * reads the conversations again each time it opens, as it did. Each read is a
 * plain idempotent GET, so it declares `"safe-read"`.
 *
 * @writers `ReportingAgentStore.handleDashboardReady`, through
 * `markGeneratedReportsStale`: a report the chat just built.
 */

type Reads = Pick<
  ReportingAgentService,
  "listDashboards" | "listConversations" | "getDashboardHtml" | "getDashboardScreenshot"
>;

/** `[slug, uid, 'reporting-agent', 'dashboards']`. */
export function generatedReportsKey(scope: QueryScope) {
  return scopedKey(scope, "reporting-agent", "dashboards");
}

/** The reports the Teammate generated for this caller. */
export function generatedReportsQuery(service: Pick<Reads, "listDashboards">, scope: QueryScope) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: generatedReportsKey(scope),
    queryFn: scopedRead(
      (request): Promise<AiDashboardSummary[]> =>
        withinAttempt(request, (bounded) => service.listDashboards(scope.tenant, bounded)),
      { retry: "safe-read" },
    ),
  });
}

/**
 * Start the generated-reports read for the Reports route loader, so it rides
 * alongside the built list rather than after it (NG-1566). Never rejects.
 */
export function prefetchGeneratedReports(
  service: Pick<Reads, "listDashboards">,
  slug: string,
): void {
  const scope = scopeIfReady(slug, "generated reports");
  if (scope) void getQueryClient().prefetchQuery(generatedReportsQuery(service, scope));
}

/**
 * The chat just built a report the list on hand predates (NG-1948): mark it
 * stale, so the Reports page reads it again (now, if it is on screen) and keeps
 * the old rows until the new answer lands.
 */
export function markGeneratedReportsStale(slug: string): void {
  const client = peekQueryClient();
  const scope = scopeIfReady(slug, "generated reports");
  if (!client || !scope) return;
  client.invalidateQueries({ queryKey: generatedReportsKey(scope) }).catch((error: unknown) => {
    console.warn("[reporting-agent] re-reading the generated reports failed", { slug, error });
  });
}

/** `[slug, uid, 'reporting-agent', 'conversations']`. */
function conversationsKey(scope: QueryScope) {
  return scopedKey(scope, "reporting-agent", "conversations");
}

/** The caller's past conversations, newest-active first: the history picker. */
export function conversationsQuery(service: Pick<Reads, "listConversations">, scope: QueryScope) {
  return queryOptions({
    ...FRESHNESS.reference,
    // Read again each time the picker opens, the list on hand painting meanwhile.
    staleTime: 0,
    queryKey: conversationsKey(scope),
    queryFn: scopedRead(
      (request): Promise<ReportingConversationSummary[]> =>
        withinAttempt(request, (bounded) => service.listConversations(scope.tenant, bounded)),
      { retry: "safe-read" },
    ),
  });
}

/** `[slug, uid, 'reporting-agent', 'artifact', id, part]`. */
function artifactKey(scope: QueryScope, artifactId: string, part: "html" | "screenshot") {
  return scopedKey(scope, "reporting-agent", "artifact", artifactId, part);
}

/** A generated report's inert HTML document, for the sandboxed frame. */
export function artifactHtmlQuery(
  service: Pick<Reads, "getDashboardHtml">,
  scope: QueryScope,
  artifactId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: artifactKey(scope, artifactId, "html"),
    queryFn: scopedRead((request) => service.getDashboardHtml(scope.tenant, artifactId, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * A generated report's preview screenshot, or `null` when there is none to
 * show. The agent captures one best-effort, so a 404 is the ordinary "no
 * preview" answer, and is silent. The preview is optional, so any other
 * failure is degradation, not an error: it warns and answers `null` too.
 */
export function artifactScreenshotQuery(
  service: Pick<Reads, "getDashboardScreenshot">,
  scope: QueryScope,
  artifactId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: artifactKey(scope, artifactId, "screenshot"),
    queryFn: scopedRead(
      async (request): Promise<Blob | null> => {
        try {
          return await service.getDashboardScreenshot(scope.tenant, artifactId, request);
        } catch (error) {
          if (httpStatus(error) !== 404) {
            console.warn("[reporting-agent] dashboard screenshot unavailable", {
              artifactId,
              error,
            });
          }
          return null;
        }
      },
      { retry: "safe-read" },
    ),
  });
}
